import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from '__components/Inputs/Select';
import MultiSelect from '__components/Inputs/MultiSelect';
import { useTranslation } from 'react-i18next';
import { useMenuScroller } from '../useMenuScroller';
import {
  AssistantField,
  runObjectExpression,
  runOptionExpression,
  runStringExpression,
  SelectOption,
} from '@kizenapps/engine';
import { useDebounce } from 'react-use';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { getHash } from '@kizenapps/engine/util';
import { handleAxiosResponseForPluginCall } from 'ts-components/Plugins/network';
import { useToast } from '__components/ToastProvider';

export const SelectBlock = ({
  field,
  pluginApiName,
  disabled = false,
  disabledIfDynamic = false,
}: {
  field: AssistantField;
  pluginApiName: string;
  disabled?: boolean;
  disabledIfDynamic?: boolean;
}) => {
  const {
    state,
    setState,
    evaluateExpression,
    shouldHideField,
    getFieldErrorState,
    inferencePending,
    initialExpressionsPending,
    expressionsIdle,
    afterFieldChange,
    registerFieldResetter,
    disabledKeys,
  } = useSetupAssistant();

  const [showToast] = useToast();

  const readyForRequests =
    !inferencePending && !initialExpressionsPending && expressionsIdle;

  const [inputValue, setInputValue] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [dynamicOptionsCache, setDynamicOptionsCache] = useState<
    Record<string, Array<SelectOption>>
  >({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const contextCache = useRef<Record<string, any>>({});

  useDebounce(() => setDebouncedSearch(inputValue), DEFAULT_INPUT_DELAY, [
    inputValue,
  ]);

  const debouncePending =
    field.typeahead && inputValue && inputValue !== debouncedSearch;

  const showLoadingState = optionsLoading || debouncePending;

  const isDynamic = Boolean(field.getFetchUrl || field.optionMapper);

  const value = (state as any)[field.key]?.value ?? field.default ?? '';

  const { t } = useTranslation();

  const isDisabled =
    disabledKeys.includes(field.key) ||
    disabled ||
    (isDynamic && disabledIfDynamic);

  const setSelectValue = useCallback(
    (newValue: SelectOption | Array<SelectOption>) => {
      setState((prevState: any) => ({
        ...prevState,
        [field.key]: {
          type: 'select',
          value: newValue,
        },
      }));

      afterFieldChange(field.key);
    },
    [afterFieldChange, field.key, setState]
  );

  useEffect(() => {
    registerFieldResetter(field.key, () => {
      setState((prevState: any) => {
        const newState = { ...prevState };
        delete newState[field.key];
        return newState;
      });
    });
  }, [field.key, registerFieldResetter, setState]);

  const fetchOptions = useCallback(
    async (state: Record<string, any>, stateHash: number) => {
      const rawUrl = field.getFetchUrl;
      const contextUrl = field.getContextUrl;
      const method = field.fetchMethod || 'GET';
      const lowercaseMethod = method.toLowerCase() as 'get' | 'post';

      setOptionsLoading(true);

      if (Object.hasOwn(dynamicOptionsCache, stateHash)) {
        // Already fetched these options
        setOptionsLoading(false);
        return;
      }

      let contextResult = contextCache.current[field.key];

      if (contextUrl && !contextResult) {
        const contextUrlResult = await runStringExpression(contextUrl, {
          __kizen_state: {
            value: {
              ...state,
              pluginApiName,
            },
          },
        });

        try {
          if (contextUrlResult && contextUrlResult.startsWith('/')) {
            contextResult = await handleAxiosResponseForPluginCall(
              lowercaseMethod,
              contextUrlResult
            );
          } else {
            let headers: any = {
              'Content-Type': 'application/json',
            };
            let body;

            if (field.getHeaders) {
              headers = await runObjectExpression(field.getHeaders, {
                __kizen_state: {
                  value: {
                    ...state,
                    pluginApiName,
                  },
                },
              });
            }

            if (method === 'POST' && field.getBody) {
              body = await runObjectExpression(field.getBody, {
                __kizen_state: {
                  value: {
                    ...state,
                    pluginApiName,
                  },
                },
              });
            }

            if (contextUrlResult) {
              contextResult = await fetch(contextUrlResult, {
                method: method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
              });

              if (contextResult.ok) {
                contextResult = await contextResult.json();
              } else {
                throw new Error('Error fetching options');
              }
            }
          }
        } catch (ex) {
          showToast({
            variant: 'failure',
            message: t(
              'Error fetching options for {{fieldName}}: {{message}}',
              {
                fieldName: field.label,
                message: (ex as any)?.message ?? t('Unknown error'),
              }
            ),
          });
          setOptionsLoading(false);
          return;
        }
      }

      contextCache.current[field.key] = contextResult;

      let urlResult = '';

      if (rawUrl) {
        urlResult = await runStringExpression(rawUrl, {
          __kizen_state: {
            value: {
              ...state,
              context: contextResult,
              pluginApiName,
            },
          },
        });
      }

      try {
        let apiResult;

        if (urlResult && urlResult.startsWith('/')) {
          apiResult = await handleAxiosResponseForPluginCall(
            lowercaseMethod,
            urlResult
          );
        } else {
          let headers: any = {
            'Content-Type': 'application/json',
          };
          let body;

          if (field.getHeaders) {
            headers = await runObjectExpression(field.getHeaders, {
              __kizen_state: {
                value: {
                  ...state,
                  context: contextResult,
                  pluginApiName,
                },
              },
            });
          }

          if (method === 'POST' && field.getBody) {
            body = await runObjectExpression(field.getBody, {
              __kizen_state: {
                value: {
                  ...state,
                  context: contextResult,
                  pluginApiName,
                },
              },
            });
          }

          if (urlResult) {
            apiResult = await fetch(urlResult, {
              method: method,
              headers,
              body: body ? JSON.stringify(body) : undefined,
            });

            if (apiResult.ok) {
              apiResult = await apiResult.json();
            } else {
              throw new Error('Error fetching options');
            }
          }
        }

        if (field.optionMapper) {
          const mappedResult = await runOptionExpression(field.optionMapper, {
            __kizen_state: {
              value: {
                ...state,
                result: apiResult,
                context: contextResult,
                pluginApiName,
              },
            },
          });

          const mapped = structuredClone(mappedResult);

          setDynamicOptionsCache((prev) => ({
            ...prev,
            [stateHash]: mapped,
          }));
        } else {
          if (Array.isArray(apiResult)) {
            setDynamicOptionsCache((prev) => ({
              ...prev,
              [stateHash]: apiResult,
            }));
          } else {
            throw new Error('API result is not an array');
          }
        }
      } catch (ex) {
        setDynamicOptionsCache((prev) => ({
          ...prev,
          [stateHash]: [],
        }));
      } finally {
        setOptionsLoading(false);
      }
    },
    [
      field.getFetchUrl,
      field.optionMapper,
      dynamicOptionsCache,
      field.fetchMethod,
      field.getHeaders,
      field.getBody,
      field.getContextUrl,
      pluginApiName,
      field.key,
      showToast,
      t,
      field.label,
    ]
  );

  const handleChangeInput = useCallback((text: string) => {
    setInputValue(text);
  }, []);

  const stateForOptionsFetch = useMemo(() => {
    return {
      ...state,
      search: debouncedSearch,
      isDisabled,
    };
  }, [state, debouncedSearch, isDisabled]);

  const stateHash = useMemo(() => {
    const partialState = structuredClone(stateForOptionsFetch);

    // The hash should not change based on the value of the current field, to prevent re-fetches when
    // choosing options in a multi-select dropdown
    delete (partialState as any)[field.key];

    return getHash(JSON.stringify(partialState));
  }, [stateForOptionsFetch, field.key]);

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  const menuProps = useMenuScroller(() => {
    setIsOpen(true);
  });

  // Handles fetching the options for a typeahead select. Should only run when the dropdown
  // is open, nothing is pending, and there is a search term
  useEffect(() => {
    if (field.typeahead && isDynamic && readyForRequests && isOpen) {
      if (debouncedSearch) {
        fetchOptions(stateForOptionsFetch, stateHash);
      }
    }
  }, [
    debouncedSearch,
    field.typeahead,
    fetchOptions,
    stateForOptionsFetch,
    isDynamic,
    stateHash,
    readyForRequests,
    isOpen,
  ]);

  // Handles fetching the options for a non-typeahead select. Should only run when the dropdown
  // is open and nothing is pending
  useEffect(() => {
    if (
      !field.typeahead &&
      isDynamic &&
      !shouldHideField(field.key) &&
      readyForRequests &&
      isOpen
    ) {
      fetchOptions(state, stateHash);
    }
  }, [
    field.typeahead,
    isDynamic,
    fetchOptions,
    state,
    stateHash,
    field.key,
    shouldHideField,
    readyForRequests,
    isOpen,
  ]);

  const dynamicOptions = useMemo(() => {
    if (isDynamic) {
      return dynamicOptionsCache[stateHash] || [];
    }

    return field.options;
  }, [isDynamic, dynamicOptionsCache, stateHash, field.options]);

  const errorState = getFieldErrorState(field.key);

  useEffect(() => {
    if (
      isDynamic &&
      !isDisabled &&
      !field.typeahead &&
      !shouldHideField(field.key) &&
      readyForRequests &&
      field.autoSelect
    ) {
      fetchOptions(state, stateHash);
    }
  }, [
    isDynamic,
    isDisabled,
    field.typeahead,
    shouldHideField,
    field.key,
    readyForRequests,
    field.autoSelect,
    fetchOptions,
    state,
    stateHash,
  ]);

  const disbleDropdownForAutoselect =
    isDynamic &&
    field.autoSelect &&
    !field.typeahead &&
    optionsLoading &&
    !value;

  useEffect(() => {
    if (
      isDynamic &&
      !isDisabled &&
      !field.typeahead &&
      !shouldHideField(field.key) &&
      dynamicOptions?.length === 1 &&
      !value &&
      field.autoSelect
    ) {
      if (field.allow_multiple) {
        setSelectValue([dynamicOptions[0]]);
      } else {
        setSelectValue(dynamicOptions[0]);
      }
    }
  }, [
    dynamicOptions,
    field.key,
    isDisabled,
    shouldHideField,
    value,
    isDynamic,
    field.typeahead,
    field.autoSelect,
    setSelectValue,
    field.allow_multiple,
  ]);

  if (shouldHideField(field.key)) {
    return null;
  }

  if (isDynamic) {
    const placeholderTextPlural =
      field.placeholder ??
      (field.typeahead
        ? t('Start Typing to Choose Options')
        : t('Choose Options'));

    const placeholderTextSingular =
      field.placeholder ??
      (field.typeahead
        ? t('Start Typing to Choose an Option')
        : t('Choose an Option'));

    const placeholderText = field.allow_multiple
      ? placeholderTextPlural
      : placeholderTextSingular;

    const noOptionsMessage = () => {
      if (!inputValue && field.typeahead) {
        return t('Start Typing to Search');
      }
      if (showLoadingState) {
        return t('Loading...');
      }
      return t('No options');
    };

    // Don't perform any filtering on the frontend, just use the API response directly:
    const filterOption = () => true;

    const onMenuClose = () => {
      setIsOpen(false);
    };

    const additionalProps = {
      options: dynamicOptions,
      value: value,
      onChange: setSelectValue,
      isSearchable: field.typeahead,
      label: field.label,
      labelInfo: field.tooltip,
      inModal: true,
      validate: errorState,
      onInputChange: handleChangeInput,
      inputValue: inputValue,
      isLoading: showLoadingState,
      placeholder: placeholderText,
      noOptionsMessage,
      filterOption,
      onMenuClose,
      disabled: isDisabled,
      required: field.required,
    };

    if (field.allow_multiple) {
      return <MultiSelect {...additionalProps} {...menuProps} />;
    }

    return <Select {...additionalProps} {...menuProps} />;
  }

  const placeholderTextPlural = field.placeholder ?? t('Choose Options');
  const placeholderTextSingular = field.placeholder ?? t('Choose an Option');

  const placeholderText = field.allow_multiple
    ? placeholderTextPlural
    : placeholderTextSingular;

  const additionalProps = {
    options: field.options,
    value: value,
    onChange: setSelectValue,
    label: field.label,
    labelInfo: field.tooltip,
    inModal: true,
    placeholder: placeholderText,
    validate: errorState,
    disabled: isDisabled || disbleDropdownForAutoselect,
    required: field.required,
  };

  if (field.allow_multiple) {
    return <MultiSelect {...additionalProps} {...menuProps} />;
  }

  return (
    <>
      <Select {...additionalProps} {...menuProps} />
    </>
  );
};
